import { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'About', href: '#about' },
    { label: 'Services', href: '#services' },
    { label: 'Gallery', href: '#gallery' },
    { label: 'Reviews', href: '#testimonials' },
    { label: 'FAQ', href: '#faq' },
    { label: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between py-4 px-6">
          {/* Logo */}
          <a
            href="#hero"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            data-testid="logo"
            className="flex items-center gap-2"
          >
            <span
              className={`font-serif text-2xl md:text-3xl font-bold tracking-tight transition-colors ${
                isScrolled ? 'text-[#0A0A0A]' : 'text-white'
              }`}
            >
              LUX
            </span>
            <span
              className={`hidden sm:block text-sm font-light tracking-widest uppercase transition-colors ${
                isScrolled ? 'text-[#71717A]' : 'text-white/80'
              }`}
            >
              Renovations
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.href)}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`text-sm font-medium tracking-wide transition-colors hover:text-[#D4AF37] ${
                  isScrolled ? 'text-[#1A1A1A]' : 'text-white'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:2403709029"
              data-testid="header-phone"
              className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                isScrolled ? 'text-[#1A1A1A]' : 'text-white'
              }`}
            >
              <Phone className="w-4 h-4" />
              <span className="hidden xl:inline">(240) 370-9029</span>
            </a>
            <Button
              onClick={() => scrollToSection('#contact')}
              data-testid="header-cta"
              className="bg-[#0A0A0A] text-white hover:bg-[#27272A] rounded-none px-6 py-2"
            >
              Get a Quote
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2 transition-colors ${
              isScrolled ? 'text-[#0A0A0A]' : 'text-white'
            }`}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden bg-white border-t border-[#E4E4E7] animate-fade-in"
          >
            <nav className="flex flex-col py-4">
              {navItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.href)}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  className="px-6 py-3 text-left text-[#1A1A1A] hover:bg-[#F4F4F5] hover:text-[#D4AF37] transition-colors"
                >
                  {item.label}
                </button>
              ))}
              <div className="px-6 py-4 border-t border-[#E4E4E7] mt-2">
                <a
                  href="tel:2403709029"
                  className="flex items-center gap-2 text-[#1A1A1A] mb-4"
                >
                  <Phone className="w-4 h-4" />
                  (240) 370-9029
                </a>
                <Button
                  onClick={() => scrollToSection('#contact')}
                  data-testid="mobile-cta"
                  className="w-full bg-[#0A0A0A] text-white hover:bg-[#27272A] rounded-none"
                >
                  Get a Free Quote
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
